import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
} from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Badge } from "ui/badge";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Sparkles,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Save,
  Building2,
  DoorOpen,
} from "lucide-react";
import { toast } from "sonner";
import {
  getAmenities,
  createAmenity,
  updateAmenity,
  deleteAmenity,
} from "services/amenityService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface Amenity {
  id: string;
  code: string;
  amenityName: string;
  amenityType: "Room" | "Property" | "Both";
  status: "active" | "inactive";
  createdOn: string;
}

export const RoomAmenitiesMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Amenity | null>(null);
  const [deletingItem, setDeletingItem] = useState<Amenity | null>(null);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(false);

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon } = useScreenPermission();

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    code: "",
    amenityName: "",
    amenityType: "Room" as "Room" | "Property" | "Both",
    status: "active" as "active" | "inactive",
  });

  const [editFormData, setEditFormData] = useState({
    code: "",
    amenityName: "",
    amenityType: "Room" as "Room" | "Property" | "Both",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    code: "",
    amenityName: "",
    amenityType: "",
  });

  const [editErrors, setEditErrors] = useState({
    code: "",
    amenityName: "",
    amenityType: "",
  });

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      const res = await getAmenities({
        searchText: searchTerm || null,
        pageNo,
        pageSize,
      });

      const mappedData: Amenity[] = res.items.map((item) => ({
        id: item.publicId,
        code: item.code,
        amenityName: item.amenityName,
        amenityType: item.amenityType,
        status: item.isActive ? "active" : "inactive",
        createdOn: item.createdAt,
      }));

      setAmenities(mappedData);
      setTotalCount(res.totalCount);
    } catch (error) {
      toast.error("Failed to load amenities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchAmenities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Apply sorting
  const filteredData = amenities.filter((item) => {
    const search = searchTerm.toLowerCase();

    return (
      item.code.toLowerCase().includes(search) ||
      item.amenityName.toLowerCase().includes(search) ||
      item.amenityType.toLowerCase().includes(search)
    );
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aValue: any = a[sortColumn as keyof Amenity];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bValue: any = b[sortColumn as keyof Amenity];

    if (typeof aValue === "string") aValue = aValue.toLowerCase();
    if (typeof bValue === "string") bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;

    return 0;
  });

  const AMENITY_CODE_REGEX = /^[A-Za-z0-9]{1,10}$/;
  const AMENITY_NAME_REGEX = /^[A-Za-z/ ]{1,20}$/;

  const handleAdd = async () => {
    const newErrors = {
      code: "",
      amenityName: "",
      amenityType: "",
    };

    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!AMENITY_CODE_REGEX.test(formData.code)) {
      newErrors.code =
        "Code can contain only letters and numbers (maximum 10 characters).";
    }

    if (!formData.amenityName.trim()) {
      newErrors.amenityName = "Amenity Name is required";
    } else if (!AMENITY_NAME_REGEX.test(formData.amenityName.trim())) {
      newErrors.amenityName =
        "Amenity Name can contain only letters, spaces and '/' (maximum 20 characters).";
    }

    if (!formData.amenityType) {
      newErrors.amenityType = "Amenity type is required";
    }

    setErrors(newErrors);

    if (newErrors.code || newErrors.amenityName || newErrors.amenityType)
      return;

    try {
      await createAmenity({
        code: formData.code,
        amenityName: formData.amenityName,
        amenityType: formData.amenityType,
        isActive: formData.status === "active",
      });

      toast.success("Amenity created successfully");

      setIsAddDialogOpen(false);
      resetForm();
      fetchAmenities();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.code?.[0];

      if (apiMessage?.toLowerCase().includes("code")) {
        setErrors((prev) => ({
          ...prev,
          code: apiMessage || "Code already exists",
        }));
      } else {
        toast.error(apiMessage || "Failed to create amenity");
      }
    }
  };

  const handleEdit = (item: Amenity) => {
    setEditingItem(item);
    setEditFormData({
      code: item.code,
      amenityName: item.amenityName,
      amenityType: item.amenityType,
      status: item.status,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingItem) return;

    const newErrors = {
      code: "",
      amenityName: "",
      amenityType: "",
    };

    if (!editFormData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!AMENITY_CODE_REGEX.test(editFormData.code)) {
      newErrors.code =
        "Code can contain only letters and numbers (maximum 10 characters).";
    }

    if (!editFormData.amenityName.trim()) {
      newErrors.amenityName = "Amenity Name is required";
    } else if (!AMENITY_NAME_REGEX.test(editFormData.amenityName.trim())) {
      newErrors.amenityName =
        "Amenity Name can contain only letters, spaces and '/' (maximum 20 characters).";
    }

    if (!editFormData.amenityType) {
      newErrors.amenityType = "Amenity type is required";
    }

    setEditErrors(newErrors);

    if (newErrors.code || newErrors.amenityName || newErrors.amenityType)
      return;

    try {
      await updateAmenity({
        publicId: editingItem.id,
        code: editFormData.code,
        amenityName: editFormData.amenityName,
        amenityType: editFormData.amenityType,
        isActive: editFormData.status === "active",
      });

      toast.success("Amenity updated successfully");

      setIsEditDialogOpen(false);
      setEditingItem(null);

      fetchAmenities();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.code?.[0];

      if (apiMessage?.toLowerCase().includes("code")) {
        setEditErrors((prev) => ({
          ...prev,
          code: apiMessage || "Code already exists",
        }));
      } else {
        toast.error(apiMessage || "Failed to update amenity");
      }
    }
  };

  const handleDelete = (item: Amenity) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      await deleteAmenity(deletingItem.id);

      toast.success("Amenity deleted successfully");

      setIsDeleteDialogOpen(false);
      setDeletingItem(null);

      fetchAmenities();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        "Failed to delete amenity";

      toast.error(apiMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      amenityName: "",
      amenityType: "Room",
      status: "active",
    });

    setErrors({
      code: "",
      amenityName: "",
      amenityType: "",
    });
  };

  const getTypeIcon = (type: string) => {
    if (type === "Room") return <DoorOpen className="w-4 h-4 text-[#5B9BD5]" />;
    if (type === "Property")
      return <Building2 className="w-4 h-4 text-[#5B9BD5]" />;
    return <Sparkles className="w-4 h-4 text-[#5B9BD5]" />;
  };

  const getTypeBadgeColor = (type: string) => {
    if (type === "Room") return "bg-blue-100 text-blue-700 border-blue-200";
    if (type === "Property")
      return "bg-purple-100 text-purple-700 border-purple-200";
    return "bg-green-100 text-green-700 border-green-200";
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search amenities by name, code, or type..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Amenity
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Table */}
        <Card className="bg-white border-[#5B9BD5]/30 hidden lg:block" style={{ marginTop: "10px", gap: "0px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("code")}
                  >
                    <div className="flex items-center gap-1">
                      Amenity Code
                      {sortColumn === "code" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("amenityName")}
                  >
                    <div className="flex items-center gap-1">
                      Amenity Name
                      {sortColumn === "amenityName" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("amenityType")}
                  >
                    <div className="flex items-center gap-1">
                      Type
                      {sortColumn === "amenityType" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("createdOn")}
                  >
                    <div className="flex items-center gap-1">
                      Created Date
                      {sortColumn === "createdOn" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-center"
                  >
                    Status
                  </TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 px-4 text-gray-500 text-sm"
                    >
                      {searchTerm
                        ? "No amenities found matching your search"
                        : "No amenities found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-2">
                        <Badge
                          variant="outline"
                          className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs"
                        >
                          {item.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            {getTypeIcon(item.amenityType)}
                          </div>
                          <span className="text-gray-900 text-sm font-medium">
                            {item.amenityName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <Badge
                          variant="outline"
                          className={`${getTypeBadgeColor(item.amenityType)} text-xs`}
                        >
                          {item.amenityType}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-700 text-sm">
                          {new Date(item.createdOn).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={`${
                            item.status === "active"
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                          } text-xs`}
                        >
                          {item.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          {canEdit && (
                            <Button
                              onClick={() => handleEdit(item)}
                              size="sm"
                              variant="ghost"
                              className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8 p-0"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              onClick={() => handleDelete(item)}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="amenities"
          />
        </Card>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {sortedData.length === 0 ? (
            <Card className="border-[#5B9BD5]/30">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">
                  {searchTerm
                    ? "No amenities found matching your search"
                    : "No amenities found"}
                </p>
              </CardContent>
            </Card>
          ) : (
            sortedData.map((item) => (
              <Card key={item.id} className="border-[#5B9BD5]/30 shadow-sm">
                <CardContent style={{ padding: "10px" }}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                        {getTypeIcon(item.amenityType)}
                      </div>
                      <div>
                        <Badge
                          variant="outline"
                          className={`${getTypeBadgeColor(item.amenityType)} text-xs`}
                          style={{ marginBottom: "4px" }}
                        >
                          {item.amenityType}
                        </Badge>
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {item.amenityName}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(item.createdOn).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Badge
                        variant="outline"
                        className={`${
                          item.status === "active"
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-gray-100 text-gray-700 border-gray-200"
                        } text-xs`}
                      >
                        {item.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                      <div className="flex justify-end border-t border-gray-100">
                        {canEdit && (
                          <Button
                            onClick={() => handleEdit(item)}
                            size="sm"
                            variant="ghost"
                            className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8"
                            style={{ padding: "0px", gap: "0px" }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            onClick={() => handleDelete(item)}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            style={{ padding: "0px", gap: "0px" }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          {totalCount > pageSize && (
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="amenities"
            />
          )}
        </div>

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add Amenity
              </DialogTitle>
              <DialogDescription>Create a new amenity</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="code"
                  value={formData.code}
                  onChange={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 10);

                    setFormData({
                      ...formData,
                      code: value,
                    });

                    setErrors({
                      ...errors,
                      code: "",
                    });
                  }}
                  placeholder="Enter amenity code"
                  className={`bg-white ${
                    errors.code ? "border-red-500" : "border-gray-200"
                  }`}
                />

                {errors.code && (
                  <p className="text-red-500 text-xs mt-1">{errors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Amenity Name"
                  required
                  id="amenityName"
                  value={formData.amenityName}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z/ ]/g, "")
                      .slice(0, 20);

                    setFormData({
                      ...formData,
                      amenityName: value,
                    });

                    setErrors({
                      ...errors,
                      amenityName: "",
                    });
                  }}
                  placeholder="Enter amenity name"
                  className={`bg-white ${
                    errors.amenityName ? "border-red-500" : "border-gray-200"
                  }`}
                />

                {errors.amenityName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.amenityName}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Amenity Type" required>
                  <Select
                    value={formData.amenityType}
                    onValueChange={(value: "Room" | "Property" | "Both") =>
                      setFormData({ ...formData, amenityType: value })
                    }
                  >
                    <SelectTriggerUI className="h-10">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="Room">Room</SelectItem>
                      <SelectItem value="Property">Property</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.amenityType && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.amenityType}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTriggerUI className="h-10">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Amenity
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Amenity
              </DialogTitle>
              <DialogDescription>Update amenity details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="edit-code"
                  value={editFormData.code}
                  onChange={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 10);

                    setEditFormData({
                      ...editFormData,
                      code: value,
                    });

                    setEditErrors({
                      ...editErrors,
                      code: "",
                    });
                  }}
                  placeholder="Enter amenity code"
                  className={`bg-white ${
                    editErrors.code ? "border-red-500" : "border-gray-200"
                  }`}
                />
                {editErrors.code && (
                  <p className="text-red-500 text-xs mt-1">{editErrors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Amenity Name"
                  required
                  id="edit-amenityName"
                  value={editFormData.amenityName}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z/ ]/g, "")
                      .slice(0, 20);

                    setEditFormData({
                      ...editFormData,
                      amenityName: value,
                    });

                    setEditErrors({
                      ...editErrors,
                      amenityName: "",
                    });
                  }}
                  placeholder="Enter amenity name"
                  className={`bg-white ${
                    editErrors.amenityName
                      ? "border-red-500"
                      : "border-gray-200"
                  }`}
                />

                {editErrors.amenityName && (
                  <p className="text-red-500 text-xs mt-1">
                    {editErrors.amenityName}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Amenity Type">
                  <Select
                    value={editFormData.amenityType}
                    onValueChange={(value: "Room" | "Property" | "Both") =>
                      setEditFormData({ ...editFormData, amenityType: value })
                    }
                  >
                    <SelectTriggerUI className="h-10">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="Room">Room</SelectItem>
                      <SelectItem value="Property">Property</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {editErrors.amenityType && (
                  <p className="text-red-500 text-xs mt-1">
                    {editErrors.amenityType}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={editFormData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setEditFormData({ ...editFormData, status: value })
                    }
                  >
                    <SelectTriggerUI className="h-10">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingItem(null);
                  setEditErrors({
                    code: "",
                    amenityName: "",
                    amenityType: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Update Amenity
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        {/* <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle style={{ color: '#5B9BD5' }}>Confirm Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this amenity?
              </DialogDescription>
            </DialogHeader>
            {deletingItem && (
              <div className="py-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{deletingItem.amenityName}</p>
                    <p className="text-sm text-gray-600">{deletingItem.amenityType} Amenity</p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog> */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Amenity</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.amenityName}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
